// src/pages/ManageCourses.js
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";

const PRIMARY = "#1F2937";

const FREQ = [
  {label:"Ad-Hoc (one-off)",days:0},
  {label:"3 Months",days:90},
  {label:"6 Months",days:180},
  {label:"12 Months",days:365},
  {label:"24 Months",days:730},
  {label:"36 Months",days:1095},
];

const STATUS_OPTIONS = [
  { value:"draft",    label:"Draft",    badge:"badge-neutral", desc:"Not visible to anyone" },
  { value:"test",     label:"Test",     badge:"badge-due",     desc:"Visible to admins and department supervisors" },
  { value:"live",     label:"Live",     badge:"badge-ok",      desc:"Published and available to trainees" },
  { value:"retired",  label:"Retired",  badge:"badge-due",     desc:"Hidden but preserved with history" },
];

// ── Availability tab — purely presentational, state lives in CourseModal ─────
function AvailabilityTab({ mode, setMode, mandatory, setMandatory, freq, setFreq }) {
  return (
    <div>
      <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:20,lineHeight:1.6}}>
        Control who can access this course and whether it is mandatory. Individual user overrides are managed from <strong>Manage Users</strong>.
      </p>

      {/* Availability */}
      <div className="form-group">
        <label>Who can access this course?</label>
        <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:6}}>
          {[
            ["available_to_all","All learners","Every active learner in the portal can see and take this course."],
            ["assigned_only",   "Assigned trainees only","Only trainees you individually assign (via Manage Users) can see this course."],
          ].map(([val,label,desc])=>(
            <label key={val} style={{display:"flex",alignItems:"flex-start",gap:12,cursor:"pointer",padding:"12px 14px",borderRadius:"var(--radius)",background:mode===val?"#E8EDF2":"var(--gray-50)",border:`1.5px solid ${mode===val?PRIMARY:"var(--gray-200)"}`,transition:"all 0.12s"}}>
              <input type="radio" name="avail_mode" value={val} checked={mode===val} onChange={()=>setMode(val)} style={{accentColor:PRIMARY,marginTop:2,flexShrink:0}}/>
              <div><div style={{fontWeight:600,fontSize:14}}>{label}</div><div style={{fontSize:12,color:"var(--gray-500)",marginTop:2}}>{desc}</div></div>
            </label>
          ))}
        </div>
      </div>

      {/* Mandatory / Optional */}
      {mode === "available_to_all" && (
        <div className="form-group">
          <label>Training type for all learners</label>
          <div style={{display:"flex",gap:10,marginTop:6}}>
            {[
              ["optional",  "Optional",  "Trainees can take this course at their discretion."],
              ["mandatory", "Mandatory", "All trainees are required to complete this course."],
            ].map(([val,label,desc])=>(
              <label key={val} style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",padding:"12px 14px",borderRadius:"var(--radius)",background:mandatory===val?"#E8EDF2":"var(--gray-50)",border:`1.5px solid ${mandatory===val?PRIMARY:"var(--gray-200)"}`,flex:1,transition:"all 0.12s"}}>
                <input type="radio" name="mandatory_mode" value={val} checked={mandatory===val} onChange={()=>setMandatory(val)} style={{accentColor:PRIMARY,marginTop:2,flexShrink:0}}/>
                <div><div style={{fontWeight:600,fontSize:13}}>{label}</div><div style={{fontSize:12,color:"var(--gray-500)",marginTop:2}}>{desc}</div></div>
              </label>
            ))}
          </div>
          <p style={{fontSize:12,color:"var(--gray-400)",marginTop:8}}>
            Individual users can be overridden to Mandatory or Optional regardless of this setting, via Manage Users.
          </p>
        </div>
      )}

      {/* Renewal frequency */}
      {(mandatory === "mandatory" || mode === "assigned_only") && (
        <div className="form-group">
          <label>Renewal Frequency</label>
          <select value={freq} onChange={e=>setFreq(parseInt(e.target.value))}>
            {FREQ.map(f=><option key={f.days} value={f.days}>{f.label}</option>)}
          </select>
          <p style={{fontSize:12,color:"var(--gray-400)",marginTop:4}}>
            {freq === 0
              ? "Ad-hoc — trainees complete this once, no re-take required."
              : `Trainees must re-complete this course every ${FREQ.find(f=>f.days===freq)?.label.toLowerCase() || freq+" days"}.`}
          </p>
          {mode === "assigned_only" && (
            <p style={{fontSize:12,color:"var(--gray-400)",marginTop:4}}>
              Applied to users assigned as Mandatory. Users assigned as Optional have no renewal.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Assignees tab ─────────────────────────────────────────────────────────────
function AssigneesTab({ courseId, availMode }) {
  const [assignees, setAssignees] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [working,   setWorking]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res  = await fetch(`/api/courses/${courseId}/assignees`);
    const data = await res.json();
    setAssignees(data.assignees || []);
    setLoading(false);
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  async function handleUnassign(email) {
    setWorking(email);
    await fetch("/api/users/assign", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ courseId, emails:[email], action:"unassign" }),
    });
    setAssignees(prev => prev.filter(a => a.email !== email));
    setWorking(null);
  }

  async function handleReset(email) {
    if (!window.confirm(`Reset training progress for ${email}?`)) return;
    setWorking(email+"-reset");
    await fetch("/api/adminreset", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ courseId, email }),
    });
    await load();
    setWorking(null);
  }

  if (loading) return <div style={{padding:32,display:"flex",justifyContent:"center"}}><span className="spinner" style={{width:22,height:22}}/></div>;

  if (availMode === "available_to_all") return (
    <div style={{textAlign:"center",padding:"32px 0",color:"var(--gray-500)",fontSize:13}}>
      <div style={{fontSize:32,marginBottom:12}}>👥</div>
      <p style={{fontWeight:600,marginBottom:6}}>Available to All Trainees</p>
      <p style={{color:"var(--gray-400)"}}>This course is visible to every active trainee. Individual assignments and overrides are managed from <strong>Manage Users</strong>.</p>
    </div>
  );

  if (!assignees.length) return (
    <div style={{textAlign:"center",padding:"32px 0",color:"var(--gray-400)",fontSize:13}}>
      <p>No users are individually assigned to this course.</p>
      <p style={{marginTop:6}}>Assign users from the <strong>Manage Users</strong> page.</p>
    </div>
  );

  return (
    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
      <thead>
        <tr style={{borderBottom:"1px solid var(--gray-200)"}}>
          {["Name","Email","Type","Status","Score","Completed",""].map((h,i)=>(
            <th key={i} style={{textAlign:"left",padding:"6px 12px 8px 0",color:"var(--gray-400)",fontWeight:600,fontSize:12}}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {assignees.map(a=>(
          <tr key={a.email} style={{borderBottom:"1px solid var(--gray-100)"}}>
            <td style={{padding:"9px 12px 9px 0",fontWeight:500}}>{a.name}</td>
            <td style={{padding:"9px 12px 9px 0",fontSize:12,color:"var(--gray-500)"}}>{a.email}</td>
            <td style={{padding:"9px 12px 9px 0"}}>
              <span className={`badge ${a.mandatory?"badge-overdue":"badge-neutral"}`} style={{fontSize:11}}>{a.mandatory?"Mandatory":"Optional"}</span>
            </td>
            <td style={{padding:"9px 12px 9px 0"}}>
              {a.progress.status==="completed"  &&<span className="badge badge-ok"      style={{fontSize:11}}>Completed</span>}
              {a.progress.status==="in_progress"&&<span className="badge badge-due"     style={{fontSize:11}}>In Progress</span>}
              {(!a.progress.status||a.progress.status==="not_started")&&<span className="badge badge-neutral" style={{fontSize:11}}>Not Started</span>}
            </td>
            <td style={{padding:"9px 12px 9px 0",color:"var(--gray-600)"}}>{a.progress.score!=null?`${a.progress.score}%`:"—"}</td>
            <td style={{padding:"9px 12px 9px 0",color:"var(--gray-600)"}}>{a.progress.completedAt?new Date(a.progress.completedAt).toLocaleDateString("en-GB"):"—"}</td>
            <td style={{padding:"9px 0",textAlign:"right"}}>
              <div style={{display:"flex",gap:4,justifyContent:"flex-end"}}>
                <button className="btn-ghost" style={{fontSize:12,padding:"3px 8px"}} onClick={()=>handleReset(a.email)} disabled={working===a.email+"-reset"}>Reset</button>
                <button className="btn-ghost" style={{fontSize:12,padding:"3px 8px",color:"var(--red)"}} onClick={()=>handleUnassign(a.email)} disabled={working===a.email}>Unassign</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Course modal ──────────────────────────────────────────────────────────────
function CourseModal({ course, depts, supervisors, callerRole, callerCategoryId, onSave, onClose }) {
  const isNew   = !course;
  const [tab,   setTab]   = useState("details");
  const [form,  setForm]  = useState(course ? {
    name:          course.name,
    description:   course.description,
    frequencyDays: course.frequencyDays,
    durationMinutes: course.durationMinutes || null,
    categoryId:  course.categoryId || "",
    managers:      course.managers     || [],
    status:        course.status       || "draft",
    version:       course.version      || "0.1",
  } : {
    name:"", description:"", frequencyDays:365, categoryId:"", managers:[], status:"draft", version:"0.1", durationMinutes:null,
  });
  const [savedId,   setSavedId]   = useState(course?.id || null);
  const [savedCourse, setSavedCourse] = useState(course || null);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState(null);
  const [warnTo,    setWarnTo]    = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [designerDirty, setDesignerDirty] = useState(false);

  // Availability tab state — lifted here so Save Course saves everything at once
  const [availMode,  setAvailMode]  = useState(course?.availabilityMode || "available_to_all");
  const [mandatory,  setMandatory]  = useState(course?.mandatoryMode    || "optional");
  const [freq,       setFreq]       = useState(course?.frequencyDays    ?? 365);

  function handleClose() {
    if (designerDirty) {
      if (!window.confirm("You have unsaved changes in the Course Designer — close anyway and lose them?")) return;
    }
    onClose();
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Categories available to this user — admin sees all
  const availableDepts = callerRole === "admin"
    ? depts
    : depts.filter(d => d.id === callerCategoryId);

  async function handleSave({ andClose = true, overrideStatus = null } = {}) {
    if (!form.name.trim())  { setError("Course name is required"); return false; }
    if (!form.categoryId)   { setError("Category is required"); return false; }
    setSaving(true); setError(null);
    try {
      const formToSave = overrideStatus ? { ...form, status: overrideStatus } : form;
      const url    = savedId ? `/api/courses/${savedId}` : "/api/courses";
      const method = savedId ? "PUT" : "POST";
      const res    = await fetch(url, { method, headers:{"Content-Type":"application/json"}, body:JSON.stringify(formToSave) });
      const d      = await res.json();
      if (!res.ok) throw new Error(d.error);
      const courseId = d.course.id;
      setSavedId(courseId);

      // Also save availability settings if course already exists
      if (courseId) {
        await fetch(`/api/courses/${courseId}/availability`, {
          method:"PUT", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({
            availabilityMode: availMode,
            mandatoryMode:    mandatory,
            frequencyDays:    mandatory === "mandatory" ? freq : 0,
          }),
        });
      }

      const updatedCourse = { ...d.course, availabilityMode: availMode, mandatoryMode: mandatory,
        frequencyDays: mandatory === "mandatory" ? freq : 0 };
      setSavedCourse(updatedCourse);
      onSave(updatedCourse);
      if (andClose) onClose();
      return courseId;
    } catch (e) { setError(e.message); return false; }
    finally { setSaving(false); }
  }

  async function handleStatusChange(newStatus) {
    if (newStatus === "test" && form.status === "draft") {
      setWarnTo("test");
      return;
    }
    if (newStatus === "live") {
      if (savedId && availMode === "available_to_all") {
        const d = await fetch(`/api/courses/${savedId}/notify-preview`).then(r=>r.json()).catch(()=>({ count:0, recipients:[] }));
        setEmailModal({
          status:         "live",
          recipientCount: d.count || 0,
          recipients:     d.recipients || [],
          templateKey:    mandatory === "mandatory" ? "course-live-mandatory" : "course-live-optional",
          mandatory:      mandatory === "mandatory",
        });
      } else {
        setWarnTo("live");
      }
      return;
    }
    set("status", newStatus);
  }

  async function handleEmailModalAction(action) {
    if (action === "cancel") { setEmailModal(null); return; }
    const em = emailModal;
    setEmailModal(null);
    // Save with the new status, close modal
    const courseId = await handleSave({ andClose: true, overrideStatus: em.status });
    if (action === "send" && courseId && em.recipients.length) {
      fetch(`/api/courses/${courseId}/notify`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ recipients: em.recipients, templateKey: em.templateKey }),
      }).catch(() => {});
    }
  }

  function toggleManager(email) {
    setForm(f => ({ ...f, managers: f.managers.includes(email) ? f.managers.filter(m => m !== email) : [...f.managers, email] }));
  }

  const tabs = [
    { id:"details", label:"Details" },
    ...(savedId ? [
      { id:"availability", label:"Availability" },
      { id:"assignees",   label:"Assignees" },
      { id:"design",      label:"🎨 Design" },
    ] : []),
  ];

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: tab==="design" ? "min(95vw,1100px)" : 600, maxHeight:"95vh", display:"flex", flexDirection:"column" }}>
        <div className="modal-header" style={{ flexShrink:0 }}>
          <h2>{isNew ? "Add Course" : "Edit Course"}</h2>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div className="editor-mode-toggle">
              {tabs.map(t => (
                <button key={t.id} type="button" className={`mode-btn ${tab===t.id?"active":""}`} onClick={() => setTab(t.id)}>
                  {t.label}
                </button>
              ))}
            </div>
            <button className="btn-ghost" onClick={handleClose}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="18"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </button>
          </div>
        </div>

        {tab === "details" && (
          <div className="modal-body" style={{ overflowY:"auto" }}>
            {/* Name */}
            <div className="form-group">
              <label>Course Name <span style={{ color:"var(--red)" }}>*</span></label>
              <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Health & Safety Awareness" />
            </div>

            {/* Description */}
            <div className="form-group">
              <label>Description</label>
              <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} />
            </div>

            {/* Department — required */}
            <div className="form-group">
              <label>Category <span style={{ color:"var(--red)" }}>*</span></label>
              {availableDepts.length === 0 ? (
                <div style={{ fontSize:13, color:"var(--red)", padding:"8px 12px", background:"#FEF2F2", borderRadius:"var(--radius)" }}>
                  You don't have a category assigned. Contact an administrator.
                </div>
              ) : (
                <select value={form.categoryId} onChange={e => set("categoryId", e.target.value)}>
                  <option value="">— Select category —</option>
                  {availableDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              )}
            </div>

            {/* Status + Version */}
            <div className="form-row">
              <div className="form-group" style={{ margin:0 }}>
                <label>Status</label>
                <select value={form.status}
                  disabled={isNew || !savedId}
                  onChange={e => handleStatusChange(e.target.value)}>
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                {(isNew || !savedId) && <div style={{ fontSize:12, color:"var(--gray-400)", marginTop:4 }}>Save first to change status</div>}
                {savedId && (
                  <div style={{ fontSize:12, color:"var(--gray-500)", marginTop:4 }}>
                    {STATUS_OPTIONS.find(s => s.value === form.status)?.desc}
                  </div>
                )}
              </div>
              <div className="form-group" style={{ margin:0 }}>
                <label>Version</label>
                <input value={form.version} onChange={e => set("version", e.target.value)} placeholder="e.g. 1.0" />
              </div>
            </div>

            {/* Frequency + Time to complete */}
            <div className="form-row">
              {form.status !== "draft" && (
                <div className="form-group" style={{ margin:0 }}>
                  <label>Renewal Frequency <span style={{fontSize:12,color:"var(--gray-400)",fontWeight:400}}>(mandatory only)</span></label>
                  <select value={form.frequencyDays} onChange={e => set("frequencyDays", parseInt(e.target.value))}>
                    {FREQ.map(f => <option key={f.days} value={f.days}>{f.label}</option>)}
                  </select>
                  <div style={{fontSize:12,color:"var(--gray-400)",marginTop:4}}>Ignored for optional courses.</div>
                </div>
              )}
              <div className="form-group" style={{ margin:0 }}>
                <label>Time to Complete <span style={{fontSize:12,color:"var(--gray-400)",fontWeight:400}}>(minutes)</span></label>
                <input type="number" min={1} max={480} value={form.durationMinutes||""} onChange={e=>set("durationMinutes",parseInt(e.target.value)||null)} placeholder="e.g. 30"/>
                <div style={{fontSize:12,color:"var(--gray-400)",marginTop:4}}>Shown to trainees on their training page.</div>
              </div>
            </div>

            {/* Manager assignment — admin only */}
            {callerRole === "admin" && supervisors.filter(s=>s.role==="supervisor").length > 0 && (
              <div className="form-group">
                <label>Assigned Supervisors <span style={{ fontSize:11, color:"var(--gray-400)", fontWeight:400 }}>(can manage this course)</span></label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:6 }}>
                  {supervisors.filter(s=>s.role==="supervisor").map(s => (
                    <label key={s.email} style={{
                      display:"flex", alignItems:"center", gap:6, cursor:"pointer", fontWeight:400,
                      background: form.managers.includes(s.email) ? "#E8EDF2" : "var(--gray-50)",
                      border: `1px solid ${form.managers.includes(s.email) ? PRIMARY : "var(--gray-200)"}`,
                      borderRadius:"var(--radius)", padding:"5px 10px", fontSize:13,
                    }}>
                      <input type="checkbox" checked={form.managers.includes(s.email)} onChange={() => toggleManager(s.email)}
                        style={{ width:"auto", accentColor:PRIMARY }} />
                      {s.name || s.email}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "availability" && savedId && (
          <div className="modal-body" style={{ overflowY:"auto" }}>
            <AvailabilityTab
              mode={availMode} setMode={setAvailMode}
              mandatory={mandatory} setMandatory={setMandatory}
              freq={freq} setFreq={setFreq}
            />
          </div>
        )}

        {tab === "assignees" && savedId && (
          <div className="modal-body" style={{overflowY:"auto"}}>
            <AssigneesTab courseId={savedId} availMode={availMode}/>
          </div>
        )}

        {tab === "design" && savedId && (
          <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>
            <CourseDesignerEmbed courseId={savedId} courseName={form.name}
              onClose={onClose}
              onDirtyChange={setDesignerDirty}
            />
          </div>
        )}

        {/* Unified footer — shown on all tabs except Design */}
        {tab !== "design" && (
          <div style={{ flexShrink:0, padding:"12px 24px", borderTop:"1px solid var(--gray-200)", background:"#fff", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
            <div>
              {error && <div style={{ fontSize:13, color:"var(--red)" }}>{error}</div>}
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button className="btn-secondary" onClick={handleClose}>Cancel</button>
              <button className="btn-primary" style={{ background:PRIMARY }} onClick={() => handleSave({ andClose: true })}
                disabled={saving || !form.name.trim() || !form.categoryId}>
                {saving ? "Saving…" : savedId ? "Save Course" : "Create Course"}
              </button>
            </div>
          </div>
        )}

        {/* Test mode warning */}
        {warnTo === "test" && (
          <div className="modal-backdrop" style={{ zIndex:1000 }}>
            <div className="modal" style={{ maxWidth:420 }}>
              <div className="modal-header"><h2>🧪 Enable Test Mode?</h2></div>
              <div className="modal-body">
                <p style={{ fontSize:14, lineHeight:1.7, color:"var(--gray-700)", marginBottom:16 }}>
                  Test mode makes the course visible to admins and department supervisors only — useful for reviewing before publishing.
                </p>
                <div className="form-actions">
                  <button className="btn-secondary" onClick={() => setWarnTo(null)}>Cancel</button>
                  <button className="btn-primary" style={{ background:PRIMARY }}
                    onClick={() => { set("status","test"); setWarnTo(null); }}>Enable Test Mode</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Live publish + email modal */}
        {emailModal && (
          <div className="modal-backdrop" style={{ zIndex:1000 }}>
            <div className="modal" style={{ maxWidth:500 }}>
              <div className="modal-header">
                <h2>📢 Publishing Course</h2>
              </div>
              <div className="modal-body">
                <p style={{ fontSize:14, lineHeight:1.7, color:"var(--gray-700)", marginBottom:16 }}>
                  This course is set to <strong>Available to All</strong>
                  {emailModal.mandatory && <> and is <strong>Mandatory</strong></>}.
                  Publishing it will make it visible to all active learners.
                </p>
                {emailModal.recipientCount > 0 ? (
                  <div style={{ background:"#EFF6FF", border:"1px solid #93C5FD", borderRadius:8, padding:"12px 16px", marginBottom:20 }}>
                    <div style={{ fontWeight:600, fontSize:14, color:"#1D4ED8", marginBottom:4 }}>
                      📧 {emailModal.recipientCount} trainee{emailModal.recipientCount !== 1 ? "s" : ""} would receive a notification email
                    </div>
                    <div style={{ fontSize:13, color:"#1E40AF" }}>
                      {emailModal.mandatory
                        ? "Alerting them this is mandatory training requiring completion."
                        : "Letting them know optional training is now available."}
                    </div>
                  </div>
                ) : (
                  <div style={{ background:"var(--gray-50)", border:"1px solid var(--gray-200)", borderRadius:8, padding:"12px 16px", marginBottom:20, fontSize:13, color:"var(--gray-500)" }}>
                    No trainees currently need notification — all have already completed this course or no trainees are registered yet.
                  </div>
                )}
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {emailModal.recipientCount > 0 && (
                    <button className="btn-primary" style={{ background:PRIMARY }}
                      onClick={() => handleEmailModalAction("send")}>
                      ✉ Set to live and send {emailModal.recipientCount} email{emailModal.recipientCount !== 1 ? "s" : ""}
                    </button>
                  )}
                  <button className="btn-secondary"
                    onClick={() => handleEmailModalAction("skip")}>
                    {emailModal.recipientCount > 0 ? "Set to live without sending emails" : "✓ Set to live"}
                  </button>
                  <button className="btn-ghost" style={{ color:"var(--red)", fontWeight:600 }}
                    onClick={() => handleEmailModalAction("cancel")}>
                    Do not change status
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Simple live warning — for assigned_only courses */}
        {warnTo === "live" && (
          <div className="modal-backdrop" style={{ zIndex:1000 }}>
            <div className="modal" style={{ maxWidth:440 }}>
              <div className="modal-header"><h2>⚠ Publish Course?</h2></div>
              <div className="modal-body">
                <p style={{ fontSize:14, lineHeight:1.7, color:"var(--gray-700)", marginBottom:16 }}>
                  Changing to Live will make this course visible to assigned trainees. Email notifications are sent when users are individually assigned.
                </p>
                <div className="form-actions">
                  <button className="btn-secondary" onClick={() => setWarnTo(null)}>Cancel</button>
                  <button className="btn-primary" style={{ background:PRIMARY }}
                    onClick={() => { set("status","live"); setWarnTo(null); }}>Yes, Publish</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CourseDesignerEmbed({ courseId, courseName, onClose, onDirtyChange }) {
  const [Comp, setComp] = useState(null);
  useEffect(() => { import("./CourseDesigner").then(m => setComp(() => m.default)); }, []);
  if (!Comp) return <div style={{ padding:32, display:"flex", justifyContent:"center" }}><span className="spinner" style={{ width:28, height:28 }}/></div>;
  return <Comp courseId={courseId} courseName={courseName} onClose={onClose} onDirtyChange={onDirtyChange} primaryColor={PRIMARY} />;
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ManageCourses() {
  const { user }    = useAuth();
  const [courses,   setCourses]  = useState([]);
  const [depts,     setDepts]    = useState([]);
  const [supers,    setSupers]   = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [modal,     setModal]    = useState(null);
  const [previewCourse, setPreviewCourse] = useState(null);
  const [PreviewComp, setPreviewComp] = useState(null);
  const [deleting,  setDeleting] = useState(null);

  async function load() {
    const [c, d, u] = await Promise.all([
      fetch("/api/courses").then(r => r.json()),
      fetch("/api/categories").then(r => r.json()),
      user.role === "admin" ? fetch("/api/users?role=supervisor").then(r => r.json()) : Promise.resolve({ users:[] }),
    ]);
    setCourses(c.courses || []);
    setDepts(d.categories || []);
    setSupers(u.users || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    import("./CourseDesigner").then(m => setPreviewComp(() => m.CoursePreview));
  }, []);

  function handleSaved(course) {
    setCourses(prev => {
      const i = prev.findIndex(c => c.id === course.id);
      return i >= 0 ? prev.map((c, j) => j === i ? course : c) : [...prev, course];
    });
  }

  async function handleDelete(course) {
    if (!window.confirm(`Move "${course.name}" to the recycle bin?`)) return;
    setDeleting(course.id);
    try {
      await fetch(`/api/courses/${course.id}`, { method:"DELETE" });
      setCourses(prev => prev.filter(c => c.id !== course.id));
    } catch (e) { alert(e.message); }
    finally { setDeleting(null); }
  }

  const deptName = id => depts.find(d => d.id === id)?.name || "—";

  if (loading) return <div className="page-loading"><span className="spinner" style={{ width:28, height:28 }}/></div>;

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Manage Courses</div><div className="page-subtitle">Upload and design training content</div></div>
        <button className="btn-primary" style={{ background:PRIMARY }} onClick={() => setModal("add")}>+ Add Course</button>
      </div>

      {courses.length === 0 ? (
        <div className="card"><div className="empty-state"><h3>No courses yet</h3><p>Click "Add Course" to create your first training course.</p></div></div>
      ) : (
        <div className="card" style={{ padding:0, overflow:"hidden" }}>
          <table className="contacts-table">
            <thead>
              <tr><th>Course</th><th>Category</th><th>Frequency</th><th>Availability</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {courses.map(course => (
                <tr key={course.id} className="contact-row">
                  <td className="contact-cell">
                    <div className="contact-name">{course.name}</div>
                    {course.description && <div className="contact-jobtitle">{course.description}</div>}
                  </td>
                  <td className="contact-cell" style={{ fontSize:13, color:"var(--gray-500)" }}>{deptName(course.categoryId)}</td>
                  <td className="contact-cell" style={{ fontSize:13, color:"var(--gray-500)" }}>
                    {FREQ.find(f => f.days === course.frequencyDays)?.label || `${course.frequencyDays}d`}
                  </td>
                  <td className="contact-cell">
                    {course.availabilityMode === "assigned_only"
                      ? <span className="badge badge-neutral" style={{ fontSize:11 }}>👥 {course.assignedUsers?.length || 0} assigned</span>
                      : <span className="badge badge-neutral" style={{ fontSize:11 }}>🌐 All trainees</span>
                    }
                  </td>
                  <td className="contact-cell">
                    {(() => {
                      const s = STATUS_OPTIONS.find(s => s.value === (course.status || "draft"));
                      return <><span className={`badge ${s?.badge}`}>{s?.label}</span>{course.version && <span style={{ fontSize:11, color:"var(--gray-400)", marginLeft:6 }}>v{course.version}</span>}</>;
                    })()}
                  </td>
                  <td className="contact-cell">
                    <div style={{ display:"flex", gap:4, justifyContent:"flex-end", flexWrap:"wrap" }}>
                      <button className="btn-ghost" style={{ fontSize:13, padding:"4px 10px" }} onClick={() => setModal(course)}>Edit</button>
                      {course.hasDesign && <button className="btn-ghost" style={{ fontSize:13, padding:"4px 10px" }} onClick={async () => {
                        const d = await fetch(`/api/courses/${course.id}/design`).then(r=>r.json());
                        setPreviewCourse({ ...course, courseDesign: d.courseDesign });
                      }}>Preview</button>}
                      <button className="btn-ghost" style={{ fontSize:13, padding:"4px 10px", color:"var(--red)" }} onClick={() => handleDelete(course)} disabled={deleting === course.id}>
                        {deleting === course.id ? "…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {previewCourse && PreviewComp && (
        <PreviewComp
          design={previewCourse.courseDesign}
          completionMode={previewCourse.completionMode || "last_page"}
          onClose={() => setPreviewCourse(null)}
          preview
        />
      )}

      {modal && (
        <CourseModal
          course={modal === "add" ? null : modal}
          depts={depts}
          supervisors={supers}
          callerRole={user.role}
          callerCategoryId={user.categoryId}
          onSave={handleSaved}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
